import { RessortEntry, RessortModuleEntry, Platform } from "../helper/interfaces";
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";

import { useRouter } from "next/router";
import { getSpecificLink } from "../helper/link";
import {  PageType, AppTheme, LinkType } from "../helper/enums";
import { getHashParameter, scrollToArticle, scrollToModule, scrollToModuleArticle } from "../helper/hashHelper";
import { AppContext } from "../provider/AppContextProvider";
import { loadModuleClientSide, loadPersonalRessortClientSide } from "../helper/api";
import { PageContent } from "../components/shared/PageContent";
import { closePianoOffer } from "../helper/piano";
import AdDebug from "./AdDebug";
import StickyAd from "./StickyAd";
import { useLoggedRouter } from "../helper/useLoggedRouter";
import BreakingNewsRessortComponent from "./BreakingNewsRessortComponent";
import ModuleComponent from "./ModuleComponent";
import { processPersonalResorts } from "../helper/ressort";
import { getCookie } from "../helper/cookie";
import RessortCategoryComponent from "./RessortCategoryComponent";  

declare global {
  interface Window {
    cX?: {
      callQueue?: unknown[];
      getUserId?: () => string | undefined;
    };
  }
}
interface Data {
  entry: RessortEntry;
  id?: string;
}

function getTeaserIdList(modules: any) {
  if(modules.length === 0) {
    return "";
  }
  return modules
    .map((m) =>
      (m?.teasers ?? [])
        .filter((m) => m.id.startsWith("1"))
        .map((t) => t.id)
        .join(",")
    )
    .filter((m) => m && m.length > 0)
    .join(",");
}

function RessortComponent(data: Data) {
  const router = useRouter();
  const [routerPush, routerReplace] = useLoggedRouter();

  const [appLocation, setAppLocation] = useState("");

  const { user, setPageData, personalResorts, debugLogs, subscription, debugLog, appInfo } = React.useContext(
    AppContext
  );

  const [loadedMergedEntry, setLoadedMergedEntry] = useState(data.entry);

  const [favModulesLoaded, setFavModulesLoaded] = useState(false);

  const [page, setPage] = useState(-1);
  const [pages, setPages] = useState<Map<string, number>>(new Map());
  const [moduleSearchParams, setModuleSearchParams] = useState<Map<string, any>>(new Map());
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pianoRecommendationsLoadedRetries, setPianoRecommendationsLoadedRetries] = useState(0);

  const scrolledWithHash = useRef(false);
  const initialLoadMoreHandled = useRef(false);
  const initialLoadMoreRunning = useRef(false);

  const [initialTeaserIdList] = useState(getTeaserIdList(data?.entry?.modules ?? []));

  const isNN = process.env.NEXT_PUBLIC_APP_THEME === AppTheme.nn;
  const isHomeRessort = data?.id == process.env.NEXT_PUBLIC_HOME_RESSORT;

  const isIos = appInfo?.platform === Platform.iOS;
  const isAndroid = appInfo?.platform === Platform.Android;

  const appStoreUrl = `https://apps.apple.com/app/id${process.env.NEXT_PUBLIC_APP_STORE_ID}`;
  const playStoreUrl = `https://play.google.com/store/apps/details?id=${process.env.NEXT_PUBLIC_PLAY_STORE_PACKAGE}`;

  const handlePageLoadMore = async (hashParams) => {
    if (!initialLoadMoreHandled.current && !initialLoadMoreRunning.current) {      
      if(hashParams.pages) {
        setIsLoadingMore(true);
        initialLoadMoreRunning.current= true
        
        let modules = loadedMergedEntry.modules

        let copyPages = new Map<string, number>()
        const decodedPages = decodeURIComponent(hashParams.pages)
        const splitedDecodedPages = decodedPages.split(",")

        await splitedDecodedPages.reduce(async (accumulator, entry) => {
          await accumulator
          const splittedEntry = entry.split("|")
          const moduleId = splittedEntry[0]
          const modulePage = parseInt(splittedEntry[1])
          copyPages.set(moduleId, modulePage)

          const items = [] as number[];
          for (let i = 1; i <= modulePage; i++)  {
            items.push(i)
          }

          await items.reduce(async (memo, i) => {
            await memo;
            const existingModule = modules.find(m => m.id == moduleId)
            if(existingModule) {
              let loadedModule = await loadModuleClientSide(
                data.id ?? '',
                moduleId,
                i,
                getTeaserIdList(modules ?? [])
              );
              
              if(loadedModule) {
                existingModule.teasers = existingModule.teasers.concat(loadedModule.teasers)
                existingModule.loadMore = loadedModule.loadMore
              }
            }
          }, undefined)
        }, Promise.resolve())

        setPages(copyPages)

        setLoadedMergedEntry({
          ...loadedMergedEntry,
          modules: modules,
        });

        setIsLoadingMore(false);
        initialLoadMoreRunning.current = false
        initialLoadMoreHandled.current = true
      }
    }
  };

  const handleParams = async (hashParams) => {
    if(debugLog) {
      debugLog(`handleParams.articleId, moduleId: ${hashParams?.articleId}, ${hashParams?.moduleId}`);
    }
    if (hashParams.pages && !initialLoadMoreHandled.current) {
        await handlePageLoadMore(hashParams);
    } else if (!scrolledWithHash.current && !isLoadingMore && favModulesLoaded) {
      let scrolled = false
      if(hashParams.moduleId && hashParams.articleId) {
        scrolled = await scrollToModuleArticle(hashParams.moduleId, hashParams.articleId, debugLog, isIos);
      }
      if(hashParams.articleId && !scrolled) {
        scrolled = await scrollToArticle(hashParams.articleId, debugLog, isIos);
      }
      if(hashParams.moduleId && !scrolled) {
        scrolled = await scrollToModule(hashParams.moduleId, debugLog, isIos);
      }
      scrolledWithHash.current = scrolled
    }
  }

  useEffect(() => {
    if (data.id) {
      setPageData(PageType.Ressort, data.id);
    } else {
      setPageData(PageType.Homepage, undefined);
    }

    //closePianoOffer();
    const hashParams = getHashParameter(window.location.hash);

    //final scroll handling
    handleParams(hashParams)
  }, [
    data.id,
    loadedMergedEntry,
    pages,
    isLoadingMore,
    favModulesLoaded
  ]);

  const navigate = async (id?: string, ressortId?: string, moduleId?: string, doNotStoreArticleId?: boolean) => {
    const isHome = router.asPath.split('?')[0].split('#')[0] == '/'
    let entries = [] as string[];
      for (const [key, value] of pages.entries()) {
        entries.push(key + "|" + value)
      }
      const urlPages = encodeURIComponent(entries.join(","));

    //store article is
    let baseUrl = '/'
    if (data.id != null && !isHome) {
      baseUrl =`/ressort/${data.id}`
    }

    let additions = ''

    if (urlPages) {
      additions += `ps=${urlPages}`
    }
    
    if(id && doNotStoreArticleId != true) {
      if(additions.length > 0) {
        additions += '&'
      }
      additions += `a=${id}`
    }
    
    if(moduleId) {
      if(additions.length > 0) {
        additions += '&'
      }
      additions += `m=${moduleId}`
    }

    if(additions.length > 0) {
      baseUrl += `#${additions}`
    }
    await routerReplace(baseUrl, true);
    setAppLocation(additions);

    if (id) {
      await routerPush(`/article/${id}`, false);
    } else if (ressortId) {
      await routerPush(`/ressort/${ressortId}`, false);
    }
  };

  const loadMore = async (id: string, searchParams?: any, restPage?: boolean) => {
    let modules = loadedMergedEntry.modules.map((module) => {
      if (module.id == id) {
        module.loadMore = false;
        if(restPage) {
          module.teasers = []
        }
      }
      return module;
    });

    let excludedModules = [] as RessortModuleEntry[];
    let foundExcludedModule=false;
    for(let i = 0; i < modules.length; i++) {
      if(modules[i].id == id) {
        foundExcludedModule = true;
        break;
      }
      if(!foundExcludedModule) {
        excludedModules.push(modules[i]);
      }
    }
    const excludedModulesTeaserIdList = getTeaserIdList(excludedModules);

    setIsLoadingMore(true);

    let newPage = (pages.get(id) ?? 0) + 1
    if(restPage) {
      newPage = 0
    }

    let newSearchParams = searchParams
    if(!newSearchParams) {
      newSearchParams = moduleSearchParams.get(id) ?? null
    }

    if(searchParams) {
      let copyModuleSearchParams = moduleSearchParams
      copyModuleSearchParams.set(id, searchParams)
      setModuleSearchParams(copyModuleSearchParams)
    }

    // load more data merge
    let loadedModule = await loadModuleClientSide(
      data.id ?? '',
      id,
      newPage,
      excludedModulesTeaserIdList,
      newSearchParams
    );

    setIsLoadingMore(false);

    if (loadedModule) {
      let copyPages = pages
      copyPages.set(id, newPage)
      setPages(copyPages)
      const existingModule = modules.find(m => m.id == id)
      if(existingModule) {
        existingModule.teasers = existingModule.teasers.concat(loadedModule.teasers)
        existingModule.loadMore = loadedModule.loadMore
      }
    }
    setLoadedMergedEntry({
      ...loadedMergedEntry,
      modules: modules,
    });
  };

  const updateExternalRessortData = async () => {
    const loadedModules = [] as RessortModuleEntry[];
    loadedMergedEntry?.modules?.forEach((module) => {
      loadedModules.push(module);
    });
    let tempEntry = { ...loadedMergedEntry }
    const personalRessortInModules = personalResorts?.filter(ressort => loadedModules.some(module => module.id == ressort.id)).length > 0;
    if (personalResorts && personalResorts.length > 0 && !personalRessortInModules && isHomeRessort) {
      const addedModules: RessortModuleEntry[] = [];
      for (let i = 0; i < personalResorts.length; i++) {
        let result: RessortEntry | null = await loadPersonalRessortClientSide(
          personalResorts[i].id
        );
        Array.prototype.push.apply(addedModules, processPersonalResorts(result,  personalResorts[i].id,  personalResorts[i].title));
      }

      const finalModules = [] as RessortModuleEntry[];
      for (let i = 0; i < loadedModules.length; i++) {
        if (i == 3) {
          Array.prototype.push.apply(finalModules, addedModules);
        }
        finalModules.push(loadedModules[i]);
      }
      tempEntry = { ...tempEntry, modules: finalModules };
      setFavModulesLoaded(true);
    }
    if(!isHomeRessort || personalResorts?.length == 0) {
      setFavModulesLoaded(true);
    }

    setLoadedMergedEntry(tempEntry);

    const pianoModules = await loadPianoRecommendationsModules(tempEntry.modules);
    setLoadedMergedEntry({...tempEntry, modules: pianoModules});
  };

  const loadPianoRecommendationsModules = async (modules: RessortModuleEntry[]) => {
    const previousTeaserIds = [] as string[]
    let tmpModules = [] as RessortModuleEntry[]
    if(modules.length > 0) {
      //load id from cookie
      const cXenseUserId = window.cX?.getUserId?.() || getCookie("cX_P=");
      const userId = user?.userId;
      
      const params = {}
      if(cXenseUserId) {
        params["cXenseUserId"] = cXenseUserId
      }
      if(userId) {
        params["ssoUserId"] = userId
      }
      
      if(userId || cXenseUserId) {
        for(let i = 0; i < modules.length; i++) {
          const existingModule = modules[i];
          let selfLink = getSpecificLink(existingModule.dataLinks, LinkType.Self);

          if(existingModule.teasers.length > 0) {
            previousTeaserIds.push(...existingModule.teasers.map(teaser => teaser.id))            
          }

          if(selfLink && existingModule.autoContentSize && existingModule.autoContentSize > 0) {
            const pianoModule = await loadModuleClientSide(
              data.id ?? '',
              existingModule.id,
              0,
              previousTeaserIds.join(","),//initialTeaserIdList
              params
            );

            if(pianoModule) {
              Array.prototype.push.apply(tmpModules, [{...existingModule, teasers: pianoModule?.teasers ?? []}]);
              previousTeaserIds.push(...pianoModule?.teasers.map(teaser => teaser.id) ?? []);
            }
          }
          else {
            Array.prototype.push.apply(tmpModules, [existingModule]);
          }
        }
        setPianoRecommendationsLoadedRetries(4)
      }
      else if(pianoRecommendationsLoadedRetries === 3) {
        //filter empty modules
        for(let i = 0; i < modules.length; i++) {
          const existingModule = modules[i];
          let selfLink = getSpecificLink(existingModule.dataLinks, LinkType.Self);

          if(!(selfLink && existingModule.teasers?.length == 0)) {
            Array.prototype.push.apply(tmpModules, [existingModule]);
          }
        }
        setPianoRecommendationsLoadedRetries(4)
      }
      else {
        setPianoRecommendationsLoadedRetries(pianoRecommendationsLoadedRetries + 1);
        return modules;
      }
    }
    else {
      setPianoRecommendationsLoadedRetries(pianoRecommendationsLoadedRetries + 1);
    }
    
    return tmpModules;
  };

  useEffect(() => {
    if(pianoRecommendationsLoadedRetries < 4) {
      const timeout = setTimeout(async () => {
        await updateExternalRessortData();
      }, 500);
      return () => {
        clearTimeout(timeout);
      }
      return 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pianoRecommendationsLoadedRetries]);

  useEffect(() => {
    updateExternalRessortData();
  }, [personalResorts, data.id]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return loadedMergedEntry && loadedMergedEntry.status === 200 ? (
    <>
      <PageContent>
        <BreakingNewsRessortComponent teasers={loadedMergedEntry.breakingNews} navigate={navigate}/>
        <RessortCategoryComponent categories={loadedMergedEntry.categories}/>
        {(process.env.NEXT_PUBLIC_APP_ENV === "development" && appInfo?.platform === Platform.iOS
        ) &&
        <>
          <div className="subscription-status">Debug Info Web Scrolled: {scrolledWithHash.current ? 'true' : 'false'}</div>
          <div className="subscription-status">window.location.hash: {appLocation}</div>
        </>
        }

        {isHomeRessort &&
        <div className="app-update">
          <h3>App-Update verfügbar</h3>
          <p>Um diese App weiterhin fehlerfrei und mit allen Funktionen nutzen zu können, ist eine Aktualisierung notwendig. Das dauert nur einen Moment.</p>
          {isIos && <div>Bitte aktualisieren Sie die App über den <a href={appStoreUrl} target="_blank" rel="noopener noreferrer">App Store</a>.</div>}
          {isAndroid && <div>Bitte aktualisieren Sie die App über den <a href={playStoreUrl} target="_blank" rel="noopener noreferrer">Google Play Store</a>.</div>}
        </div>}
        
        {loadedMergedEntry.modules.map((module, indexModule) => {
          return(
            <ModuleComponent key={`rm-${module.id}-${indexModule}`} module={module} moduleIndex={indexModule} navigate={navigate} 
              loadMore={loadMore} pageInformation={data.entry.pageInformation} isLoadingMore={isLoadingMore}/>
          )
        })}

        { process.env.NEXT_PUBLIC_APP_ENV != "production" && debugLogs &&
          <div>
            User {user?.userName ?? '-'} {user?.userSurname ?? '-'}
            <div className="debug-logs" dangerouslySetInnerHTML={{ __html: debugLogs }} />
          </div>
        }
        { process.env.NEXT_PUBLIC_APP_ENV != "production" &&
          <div className="subscription-status">
            <h3>Subscription Status</h3>
            <div className="subscription-details">
              <div className="subscription-type">
                <h4>PUR Subscription</h4>
                <div>Web: {subscription.purWeb ? 'Active' : 'Inactive'}</div>
                <div>App: {subscription.purApp ? 'Active' : 'Inactive'}</div>
                {subscription.purValidUntil && (
                  <div>Valid until: {new Date(subscription.purValidUntil).toLocaleDateString()}</div>
                )}
              </div>
              
              <div className="subscription-type">
                <h4>NNPlus Subscription</h4>
                <div>Web: {subscription.nnPlusWeb ? 'Active' : 'Inactive'}</div>
                <div>App: {subscription.nnPlusApp ? 'Active' : 'Inactive'}</div>
                {subscription.nnPlusValidUntil && (
                  <div>Valid until: {new Date(subscription.nnPlusValidUntil).toLocaleDateString()}</div>
                )}
              </div>

              <div className="subscription-meta">
                <div>Status: {subscription.validated ? 'Validated' : 'Not Validated'}</div>
                {subscription.purTermId && <div>PURR Term ID: {subscription.purTermId}</div>}
                {subscription.nnPlusTermId && <div>NNPlus Term ID: {subscription.nnPlusTermId}</div>}
              </div>
              <div className="subscription-meta">
                <div>pushesEnabled: {appInfo?.pushesEnabled ? 'Yes' : 'No'}</div>
                <div>name: {appInfo?.name}</div>
                <div>version: {appInfo?.version}</div>
                <div>platform: {appInfo?.platform}</div>
                <div>pushSubscriptionId: {appInfo?.pushSubscriptionId}</div>
              </div>
              <div className="subscription-meta">
                <div>appInfo.name: {appInfo.name}</div>
                <div>appInfo.version: {appInfo.version}</div>
                <div>appInfo.platform: {appInfo.platform}</div>
                <div>appInfo.pushSubscriptionId: {appInfo.pushSubscriptionId}</div>
                <div>appInfo.pushesEnabled: {appInfo.pushesEnabled ? 'Yes' : 'No'}</div>
                <div>appInfo.iapAvailable: {appInfo.iapAvailable ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>
        }
        <AdDebug />
        <StickyAd/>
      </PageContent>
    </>
  ) : (
    <></>
  );
}

export default RessortComponent;
